import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AspiringAuth() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if already logged in as aspiring founder
    const checkAuth = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/aspiring/auth/me`, { credentials: "include" });
        if (res.ok) {
          navigate('/aspiring/dashboard');
        }
      } catch (e) {
        console.error("Auth check failed:", e);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleGoogleLogin = () => {
    setLoading(true);
    // Use Google Auth - redirect to aspiring dashboard after auth
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/aspiring/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex flex-col">
      <div className="p-4">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">O</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Operon</h1>
            <p className="text-sm text-amber-600">Activate the right pathways</p>
            <p className="text-slate-600 mt-2">Start your biotech journey with the right foundation</p>
          </div>

          <Card className="border-2 border-slate-200">
            <CardContent className="p-6 space-y-6">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Welcome</h2>
                <p className="text-sm text-slate-600">
                  Sign in to access tools for aspiring biotech founders
                </p>
              </div>

              <Button 
                onClick={handleGoogleLogin}
                disabled={loading}
                variant="outline" 
                className="w-full h-12 gap-3 border-2"
                data-testid="google-login-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                Continue with Google
              </Button>

              <div className="text-center text-xs text-slate-500">
                By signing in, you agree to our Terms of Service and Privacy Policy
              </div>
            </CardContent>
          </Card>

          <div className="mt-8 text-center">
            <p className="text-sm text-slate-600 mb-4">What you'll get access to:</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white/60 rounded-lg p-3 text-slate-700">Profile Builder</div>
              <div className="bg-white/60 rounded-lg p-3 text-slate-700">AI Brainstorming</div>
              <div className="bg-white/60 rounded-lg p-3 text-slate-700">Cofounder Matching</div>
              <div className="bg-white/60 rounded-lg p-3 text-slate-700">Venture Builders</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
